import { useState, useEffect } from 'react';
import {
  hasPaidPlan,
  MILES_DOCUMENTATION_URL,
  MILES_SUPPORT_EMAIL,
} from '@miles/shared';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { signOut } from '../lib/auth-client';
import { BookOpen, LifeBuoy, LogOut, Settings, Share2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useCreditBalance } from '../hooks/useCreditBalance';
import { useSubscription } from '../hooks/useSubscription';
import { useReferralPartner, useReferralsNew, markReferralsSeen } from '../hooks/useReferral';
import { getBarColor, getPlanBadgeStyle } from '../lib/usage';
import { getGravatarUrl } from '../lib/gravatar';

interface UserMenuProps {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
}

export function UserMenu({ user }: UserMenuProps) {
  const navigate = useNavigate();
  const { isLoading, usagePercent, periodEnd, topUpCredits } =
    useCreditBalance();
  const { planType, subscription, plans } = useSubscription();
  const { data: partnerData } = useReferralPartner();
  const showReferrals = partnerData?.isPartner && partnerData?.hasActiveSubscription;
  const referralsNew = useReferralsNew();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFailed, setAvatarFailed] = useState(false);

  const isFree = planType === 'free';
  const isPaid = hasPaidPlan(planType);
  const currentPlan = plans.find((p) => p.type === planType);
  const planName = currentPlan?.name ?? 'Free';
  const planBadgeStyle = getPlanBadgeStyle(planType);
  const isExhausted = isFree && usagePercent >= 95;
  const renewLabel = (() => {
    if (!periodEnd) return '';
    const endDate = new Date(periodEnd);
    const daysLeft = Math.max(
      0,
      Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
    );
    const verb = subscription?.cancelAtPeriodEnd ? 'Cancels' : 'Resets';
    if (daysLeft === 0) return `${verb} today`;
    return daysLeft <= 30
      ? `${verb} in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
      : `${verb} ${endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  })();

  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    getGravatarUrl(email).then(setAvatarUrl);
  }, [user?.email]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  const initials = (user?.name || user?.email || 'U').charAt(0).toUpperCase();
  const imageUrl = !avatarFailed ? (user?.image || avatarUrl) : null;

  return (
    <div className="flex items-center gap-3">
      {/* Header upgrade CTA — only for exhausted free users */}
      {isExhausted && (
        <Button
          className="relative z-10 bg-white text-black hover:bg-white/90 border-white"
          onClick={() => navigate({ to: '/settings/plans' })}
        >
          Upgrade
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center focus:outline-none focus:ring-2 focus:ring-white/20 rounded-full">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={user.name || user.email || 'User'}
                className="w-9 h-9 rounded-full border border-white/10 hover:border-white/30 transition-colors cursor-pointer"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-neutral-700 border border-white/10 hover:border-white/30 transition-colors flex items-center justify-center text-sm font-medium text-white cursor-pointer">
                {initials}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {/* User identity header — click to go to Account settings */}
          <DropdownMenuItem
            className="py-3 cursor-pointer focus:bg-transparent"
            onClick={() => navigate({ to: '/settings/account' })}
          >
            <div className="flex items-center gap-3">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={user.name || user.email || 'User'}
                  className="w-10 h-10 rounded-full border border-white/10 shrink-0"
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-neutral-700 border border-white/10 flex items-center justify-center text-sm font-medium text-white shrink-0">
                  {initials}
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-base font-semibold leading-snug truncate">
                    {user?.name || 'User'}
                  </p>
                  <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider ${planBadgeStyle}`} style={{ fontSize: '10px' }}>
                    {planName}
                  </span>
                </div>
                <p className="text-sm leading-snug text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Compact usage indicator — click to go to Usage */}
          {!isLoading && (
            <>
              <DropdownMenuItem
                className="font-normal py-2 cursor-pointer"
                onClick={() => navigate({ to: '/settings/usage' })}
              >
                <div className="flex flex-col gap-1.5 w-full">
                  {/* Monthly credits bar */}
                  <div className="flex items-center justify-between text-xs text-white/50">
                    {topUpCredits > 0 ? (
                      <>
                        <span>{isFree ? 'Free credits' : 'Monthly'}</span>
                        <span className="tabular-nums">{usagePercent}% used</span>
                      </>
                    ) : (
                      <span className="tabular-nums">{usagePercent}% used</span>
                    )}
                    {!topUpCredits && (
                      <span>{renewLabel}</span>
                    )}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getBarColor(usagePercent)}`}
                      style={{
                        width: `${Math.min(usagePercent, 100)}%`,
                      }}
                    />
                  </div>
                  {/* Top-up credits remaining */}
                  {topUpCredits > 0 && (
                    <div className="flex items-center justify-between text-xs text-white/50 mt-1">
                      <span>Top-up</span>
                      <span className="tabular-nums">
                        {topUpCredits.toLocaleString()} credits
                      </span>
                    </div>
                  )}
                  {isFree ? (
                    <Button
                      size="sm"
                      className="w-full mt-3"
                      onClick={() => navigate({ to: '/settings/plans' })}
                    >
                      Upgrade
                    </Button>
                  ) : usagePercent >= 80 && topUpCredits === 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3"
                      onClick={() => navigate({ to: '/settings/plans' })}
                    >
                      Top-up Credits
                    </Button>
                  ) : null}
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem onClick={() => navigate({ to: '/settings/account' })}>
            <Settings className="h-[18px] w-[18px]" />
            Settings
          </DropdownMenuItem>

          {showReferrals && (
            <DropdownMenuItem onClick={() => { markReferralsSeen(); navigate({ to: '/settings/referrals' }); }}>
              <Share2 className="h-[18px] w-[18px]" />
              Referrals
              {referralsNew && (
                <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
              )}
            </DropdownMenuItem>
          )}

          <DropdownMenuItem asChild>
            <a
              href={MILES_DOCUMENTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BookOpen className="h-[18px] w-[18px]" />
              Documentation
            </a>
          </DropdownMenuItem>

          {isPaid && (
            <DropdownMenuItem asChild>
              <a href={`mailto:${MILES_SUPPORT_EMAIL}`}>
                <LifeBuoy className="h-[18px] w-[18px]" />
                Support
              </a>
            </DropdownMenuItem>
          )}

          {/* Sign out */}
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="h-[18px] w-[18px]" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
